/**
 * UserConfirmationStep — terminal summary for the per-user (member) onboarding flow, shown after
 * SAM calls finish_onboarding(). Mirrors the tenant-admin ConfirmationStep but summarizes the
 * member's own setup (name, role, interaction preference) rather than the company.
 */
import React from 'react';

interface UserConfirmationStepProps {
  name: string;
  roleName?: string;
  avatarUrl?: string;
  interactionMode: 'text' | 'voice';
  voiceGender?: 'male' | 'female' | null;
  onFinish: () => void;
  finishing?: boolean;
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-accent-text">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-ui-border last:border-0">
      <CheckIcon />
      <div className="min-w-0">
        <span className="text-xs font-medium text-ui-text3 uppercase tracking-wide block">{label}</span>
        <span className="text-sm text-ui-text font-medium break-words">{value}</span>
      </div>
    </div>
  );
}

const UserConfirmationStep: React.FC<UserConfirmationStepProps> = ({
  name,
  roleName,
  avatarUrl,
  interactionMode,
  voiceGender,
  onFinish,
  finishing = false,
}) => {
  const voiceText =
    interactionMode === 'voice'
      ? `Voice${voiceGender ? ` — ${voiceGender.charAt(0).toUpperCase() + voiceGender.slice(1)}` : ''}`
      : 'Text only';

  return (
    <div className="card-surface p-6 text-center sm:p-8">
      {/* Hero — the member's avatar if set, otherwise a check badge */}
      {avatarUrl ? (
        <div className="w-16 h-16 rounded-full overflow-hidden mx-auto mb-4 ring-1 ring-ui-border">
          <img src={avatarUrl} alt="Profile" className="h-full w-full object-cover" />
        </div>
      ) : (
        <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-8 w-8 text-accent-text">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
      )}
      <h2 className="text-h2 text-ui-text mb-1">You&rsquo;re all set!</h2>
      <p className="text-ui-text2 text-sm mb-6">Your profile is ready — SAM knows who you are.</p>

      <div className="bg-ui-subtle rounded-xl px-4 py-1 mb-6 text-left max-h-[46dvh] overflow-y-auto no-scrollbar">
        {name.trim() && <SummaryRow label="Name" value={name.trim()} />}
        {roleName && <SummaryRow label="Role" value={roleName} />}
        <SummaryRow label="Interaction" value={voiceText} />
      </div>

      <button
        onClick={onFinish}
        disabled={finishing}
        className="w-full bg-accent-fill text-on-accent py-3 rounded-xl text-sm font-semibold hover:brightness-110 transition disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {finishing ? (
          'Taking you in…'
        ) : (
          <>
            Let&rsquo;s Go!
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </>
        )}
      </button>
    </div>
  );
};

export default UserConfirmationStep;
